/**
 * Ballmate - ActiveCampaign form intercept (storefront)
 * =====================================================
 * Captures native Shopify contact-form submissions and forwards a clean payload
 * to the Cloudflare Worker, which does the ActiveCampaign sync.
 *
 * Pattern: these forms POST to /contact and full-page reload. So we stash the
 * payload on submit, then send it on the reload once `.bm-success` confirms
 * Shopify accepted it. That way we never create a contact for a submission that
 * bailed at the captcha step.
 *
 * Load order matters: this must run AFTER each section's own inline validation
 * script so an invalid submit (which preventDefault's) never reaches us. Load it
 * deferred, or paste it before </body> in theme.liquid. (Our submit listener
 * checks e.defaultPrevented, which is only reliable if validation registered
 * its listener first - i.e. we run deferred.)
 *
 * SCOPE: two forms only.
 *   - book-a-demo   -> the "Book a Live Session" form (now on /pages/book-a-live-session)
 *   - contact-form  -> the Contact page form
 * The spec sheet pop-up and the Quote Builder both sync themselves via fetch and
 * are NOT handled here.
 *
 * TWO TEMPLATE SHAPES are supported (resolveFormId handles both):
 *   1. Lead-form template (book-a-demo): data-ac-form on the .bm-form SECTION wrapper.
 *   2. Contact template (contact-form): hidden contact[form_type] input carrying the id.
 */
(function () {
  'use strict';

  var WORKER_URL = 'https://ballmate-ac-worker.techsupport-eb4.workers.dev';
  var STORAGE_KEY = 'bm_ac_pending';
  var SUCCESS_SELECTOR = '.bm-success';
  var MAX_PENDING_AGE_MS = 5 * 60 * 1000; // ignore stale stashes

  var KNOWN_FORMS = [
    'book-a-demo',
    'contact-form'
  ];

  // Book a Live Session "Your Role" dropdown -> AC buyer type.
  // "Academy or Institution" is ambiguous (academy=multiplier, institution=anchor);
  // defaulting to multiplier. "Other" leaves buyer type unset (no buyer tag applied).
  var DEMO_ROLE_MAP = {
    'Coach': 'igniter',
    'Program Leader': 'multiplier',
    'Academy or Institution': 'multiplier',
    'Other': null
  };

  // Contact form "Role" dropdown -> AC buyer type.
  // Aligned to the Journey Map setup checklist:
  //   Coach -> igniter, Program Leader -> multiplier, Procurement -> anchor.
  // Set Role Options in the customiser to exactly: Coach, Program Leader, Procurement
  var CONTACT_ROLE_MAP = {
    'Coach': 'igniter',
    'Program Leader': 'multiplier',
    'Procurement': 'anchor'
  };

  var COUNTRY_MAP = { 'Australia': 'AU', 'North America': 'US', 'Europe': 'EU' };

  var EU_COUNTRIES = ['AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU',
    'IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE','GB','NO','CH','IS'];

  // --- helpers ---------------------------------------------------------------

  function readField(form, key) {
    var el = form.querySelector('[name="contact[' + key + ']"]');
    return el ? (el.value || '').trim() : '';
  }

  // Fallback region detection from the Shopify market. With Markets subfolders
  // the URL prefix IS the market (AU = root, US = /en-us/, EU = /en-eu/), so the
  // path is the reliable signal. Used when a form has no explicit Country field.
  function detectRegionFromMarket() {
    var seg = (location.pathname.split('/')[1] || '').toLowerCase();
    var match = seg.match(/^[a-z]{2}-([a-z]{2})$/);
    var code = match ? match[1] : '';
    if (code === 'us') return 'US';
    if (code === 'eu') return 'EU';
    if (code === 'au') return 'AU';

    var country = String((window.Shopify && window.Shopify.country) || '').toUpperCase();
    if (['US', 'CA', 'MX'].indexOf(country) !== -1) return 'US';
    if (EU_COUNTRIES.indexOf(country) !== -1) return 'EU';
    return 'AU';
  }

  // --- build the normalised payload per form ---------------------------------

  function buildPayload(form, formId) {
    var email = readField(form, 'email');
    if (!email) return null;
    var firstName = readField(form, 'first_name');

    if (formId === 'book-a-demo') {
      // Book a Live Session. Per the Journey Map's "Book a Demo" mapping, buyer
      // type is role-driven (not court-driven). Courts are captured as data only.
      // NOTE: the form's Role field key is `role` (was previously mis-read as
      // `demo_role`, which is why buyer type never mapped). For a clean match,
      // set the Role dropdown options to exactly:
      //   Coach, Program Leader, Academy or Institution, Other
      var lsRole = readField(form, 'role');
      var lsBuyer = DEMO_ROLE_MAP[lsRole] || null; // graceful miss = no buyer tag
      var lsHear = readField(form, 'hear_about');
      var lsNotes = [
        lsRole ? 'Role: ' + lsRole : '',
        lsHear ? 'Heard via: ' + lsHear : ''
      ].filter(Boolean).join(' | ');

      return {
        email: email,
        firstName: firstName,
        lastName: readField(form, 'last_name'),
        phone: readField(form, 'phone'),
        // This form HAS a Country field, so use it; fall back to the market URL.
        region: COUNTRY_MAP[readField(form, 'country')] || detectRegionFromMarket(),
        buyerType: lsBuyer,
        lifecycleStage: 'Lead',
        priority: lsBuyer === 'anchor' ? 'High' : 'Medium',
        actionTags: ['live-session-requested'],
        formId: formId,
        extra: {
          message: readField(form, 'message'),
          qtyRequired: readField(form, 'number_of_courts'),
          notes: lsNotes
        }
      };
    }

    if (formId === 'contact-form') {
      // Buyer type + priority driven by the Role dropdown (no court count here).
      // Region comes from the explicit Country dropdown.
      var cfRole = readField(form, 'role');
      var cfBuyer = CONTACT_ROLE_MAP[cfRole] || null; // null = no buyer tag (graceful miss)
      var cfNotes = [
        readField(form, 'enquiry') ? 'Enquiry: ' + readField(form, 'enquiry') : '',
        cfRole ? 'Role: ' + cfRole : ''
      ].filter(Boolean).join(' | ');

      return {
        email: email,
        firstName: firstName,
        lastName: readField(form, 'last_name'),
        phone: readField(form, 'phone'),
        region: COUNTRY_MAP[readField(form, 'country')] || 'AU',
        buyerType: cfBuyer,
        lifecycleStage: 'Lead',
        priority: cfBuyer === 'anchor' ? 'High' : 'Medium', // per doc: procurement/anchor = High
        actionTags: [],                                      // doc: website-lead baseline only, no action tag
        formId: formId,
        extra: {
          organizationName: readField(form, 'organisation'),
          message: readField(form, 'body'),
          notes: cfNotes
        }
      };
    }

    return null;
  }

  // --- send on the success reload --------------------------------------------

  function sendPending() {
    var pending;
    try { pending = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null'); }
    catch (e) { pending = null; }
    if (!pending) return;
    if (!document.querySelector(SUCCESS_SELECTOR)) return; // only on a confirmed success page

    sessionStorage.removeItem(STORAGE_KEY); // clear first so we never double-send

    if (Date.now() - (pending.ts || 0) > MAX_PENDING_AGE_MS) return;
    delete pending.ts;

    fetch(WORKER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(pending)
    })
      .then(function (r) { return r.json(); })
      .then(function (d) { console.log('[bm-ac]', d); })
      .catch(function (e) { console.error('[bm-ac] send failed', e); });
  }

  // --- capture on submit -----------------------------------------------------

  // Lead-form template: data-ac-form lives on the .bm-form SECTION wrapper
  // (an ancestor of the form). Contact template: .bm-form is not the ancestor,
  // so closest('.bm-form') returns null and we fall through to the hidden
  // contact[form_type] input, which carries the id.
  function resolveFormId(form) {
    var wrapper = form.closest('.bm-form');
    if (wrapper && wrapper.dataset && wrapper.dataset.acForm) return wrapper.dataset.acForm;
    var typeEl = form.querySelector('[name="contact[form_type]"]');
    if (typeEl && typeEl.value) return typeEl.value.trim();
    return '';
  }

  function attachCapture() {
    // Scan every form, gate to contact forms (have a contact[email]) and then
    // to KNOWN_FORMS. Newsletter / other forms resolve to '' and are skipped.
    var forms = document.querySelectorAll('form');
    Array.prototype.forEach.call(forms, function (form) {
      if (!form.querySelector('[name="contact[email]"]')) return;
      var formId = resolveFormId(form);
      if (!formId || KNOWN_FORMS.indexOf(formId) === -1) return;

      form.addEventListener('submit', function (e) {
        if (e.defaultPrevented) return; // validation already rejected it
        var payload = buildPayload(form, formId);
        if (!payload) return;
        payload.ts = Date.now();
        try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch (err) {}
        // let the native submit proceed (full page reload)
      });
    });
  }

  function init() {
    sendPending();
    attachCapture();
  }

  // 'complete' means DOMContentLoaded already fired; otherwise wait for it so our
  // submit listener attaches AFTER each section's own validation listener.
  if (document.readyState === 'complete') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();